import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 *
 * Handles the products-per-row control (1, 2 or 3 columns). The chosen value is
 * written to the grid's `product-grid-view` attribute (which CSS maps to a fixed
 * column count at every breakpoint) and persisted so it survives navigation.
 *
 * The grid renders `product-grid-view="auto"` until the shopper picks a count. That
 * attribute is listed in PRESERVED_ATTRIBUTES (morph.js), so the Section Rendering API
 * carries the chosen value across filter/sort morphs without any extra bookkeeping here.
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | null} */
  #restoreObserver = null;

  /**
   * Sets up the MutationObserver that re-asserts the stored layout after morphs.
   */
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('initialized', '');

    // A Section Rendering morph normally carries `product-grid-view` untouched via
    // PRESERVED_ATTRIBUTES. But in the category-sidebar layout, <collection-layout> relocates
    // the controls bar (and re-runs that relocation through its own MutationObserver, which
    // fires mid-morph), so the live DOM diverges from the server response and the grid can be
    // rebuilt from scratch — resetting it to `auto`. Rather than chase every cause, watch the
    // results list (a stable element that survives the morph) and re-assert the shopper's
    // stored choice whenever the grid drifts from it. Cheap: the callback early-returns unless
    // a value is stored and the live grid actually disagrees.
    this.#restoreObserver = new MutationObserver(() => this.#enforceStoredLayout());
    this.#restoreObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['product-grid-view'],
    });
  }

  /**
   * Disconnects the MutationObserver.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#restoreObserver?.disconnect();
    this.#restoreObserver = null;
  }

  /**
   * Re-applies the shopper's stored column choice when the live grid no longer matches it
   * (e.g. after a morph rebuilt the grid back to the `auto` default). Pauses the observer
   * across its own write so the re-application doesn't re-trigger the callback.
   */
  #enforceStoredLayout() {
    if (window.Shopify?.designMode) return;

    const stored = sessionStorage.getItem('product-grid-view');
    if (!stored) return;

    const grid = this.querySelector('ul[product-grid-view]');
    if (!grid || grid.getAttribute('product-grid-view') === stored) return;

    this.#restoreObserver?.disconnect();
    this.#setLayout(stored);
    this.#restoreObserver?.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['product-grid-view'],
    });
  }

  /**
   * Updates the products-per-row layout from a control change.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const value = target.value;

    // Apply synchronously. product-grid-view is the live source of truth a Section Rendering
    // morph reads (via PRESERVED_ATTRIBUTES) to carry the layout across filter/sort updates.
    // Deferring the write inside a view transition let the callback be starved on a busy page
    // — or pre-empted by the filter's own transition — so a quickly-following filter morph
    // captured the stale value and reverted the grid. A synchronous write removes that race.
    this.#setLayout(value);

    // Persist for full page loads, where the inline restore script re-applies it pre-paint.
    // Skipped in the theme editor: that restore script is gated behind `unless design_mode`,
    // so a stored value would never be read back there.
    if (!window.Shopify?.designMode) {
      sessionStorage.setItem('product-grid-view', value);
    }
  }

  /**
   * Sets the grid column count and keeps the desktop and mobile control groups in sync.
   *
   * @param {string} value
   */
  #setLayout(value) {
    // Query the live grid rather than this.refs.grid: a morph can replace the grid element,
    // leaving refs stale, and #enforceStoredLayout needs to target whatever grid is current.
    const grid = this.querySelector('ul[product-grid-view]');
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);

    const options = this.querySelectorAll(
      'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
    );
    for (const option of options) {
      if (option instanceof HTMLInputElement) {
        option.checked = option.value === value;
      }
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
